import { Alert, Box, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useT } from '../i18n/useT.js';

/**
 * Photographing a document, with a viewfinder and a confirmation.
 *
 * ## Why this exists when `<input capture>` already works
 *
 * The attachments page has had a camera button since Task 10, and it is the
 * right control for what it does: one tap, the system camera, a file. What it
 * cannot do is show the picture back before it is sent.
 *
 * That matters more here than it sounds. A photographed receipt is a five-year
 * accounting record and the *only* evidence of what it documents, and a blurred
 * or half-cropped one is not discovered until somebody opens it in an audit.
 * The system camera's own preview closes the moment you accept it; by the time
 * the file reaches the app, retaking it means starting the whole flow again.
 * A viewfinder and a confirm step turn "take it again" into one tap.
 *
 * ## The fallback is not a degraded mode
 *
 * `getUserMedia` is unavailable on any page not served over https, is refused
 * outright when the person has denied camera permission, and does not exist in
 * older browsers. In every one of those cases this falls back to
 * `<input capture="environment">`, which produces exactly the same `File` and
 * has the same three-tap cost. Nothing is lost but the preview, and nothing
 * about the fallback is presented as an error, because for a laptop with no
 * camera it is simply the correct control.
 */

export interface CameraCaptureProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  /** Called once, with the photograph, when the person confirms it. */
  readonly onCapture: (file: File) => void;
  /** Shown above the viewfinder: what this photograph is for. */
  readonly title?: string | undefined;
}

/** JPEG, because a photograph is not a screenshot and PNG would be four times the bytes. */
const MIME_TYPE = 'image/jpeg';
/**
 * Quality high enough that small print survives.
 *
 * 0.8 is the usual default and visibly softens the 6pt type at the bottom of a
 * till receipt — which is where the VAT number is.
 */
const QUALITY = 0.92;

type Stage =
  | { readonly kind: 'starting' }
  | { readonly kind: 'live' }
  | { readonly kind: 'review'; readonly url: string; readonly file: File }
  /** No camera available. Not an error state — see the note above. */
  | { readonly kind: 'fallback'; readonly reason: string };

export function CameraCapture({
  opened,
  onClose,
  onCapture,
  title,
}: CameraCaptureProps): ReactNode {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'starting' });

  /**
   * Release the camera.
   *
   * Not optional housekeeping: a track left running keeps the recording
   * indicator lit and, on a phone, keeps the camera powered. Both read to the
   * person as the application watching them after they closed it.
   */
  const stopStream = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }

    let cancelled = false;
    setStage({ kind: 'starting' });

    const start = async (): Promise<void> => {
      if (navigator.mediaDevices?.getUserMedia === undefined) {
        if (!cancelled) {
          setStage({ kind: 'fallback', reason: t('camera.unsupported') });
        }
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            // The rear camera, where there is a choice. `ideal` rather than
            // `exact`: a laptop has only one camera and `exact` would fail
            // outright rather than using it.
            facingMode: { ideal: 'environment' },
            // Enough resolution to read a receipt, not so much that a modern
            // phone hands back a 12-megapixel frame to be base64-encoded.
            width: { ideal: 1920 },
            height: { ideal: 1920 },
          },
          audio: false,
        });
        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        streamRef.current = stream;
        setStage({ kind: 'live' });
      } catch (error) {
        if (cancelled) {
          return;
        }
        const name = (error as { name?: string } | null)?.name ?? '';
        setStage({
          kind: 'fallback',
          reason:
            name === 'NotAllowedError' ? t('camera.denied') : t('camera.unavailable'),
        });
      }
    };

    void start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [opened, stopStream, t]);

  /*
    Attaching the stream is its own effect, and it has to be: the <video>
    element does not exist until the modal has rendered the `live` stage, so
    doing it inside the async start above sets srcObject on null and the
    viewfinder stays black with no error anywhere.
  */
  useEffect(() => {
    if (stage.kind !== 'live') {
      return;
    }
    const video = videoRef.current;
    if (video === null || streamRef.current === null) {
      return;
    }
    video.srcObject = streamRef.current;
    void video.play().catch(() => {
      // Autoplay refusal. The element is muted and playsInline, so this is rare;
      // when it happens the frame is still grabbable from the paused video.
    });
  }, [stage.kind]);

  // Revoke the preview's object URL when it is replaced or the modal closes.
  // Without this, retaking a photo ten times leaks ten full-size images.
  useEffect(() => {
    if (stage.kind !== 'review') {
      return undefined;
    }
    const { url } = stage;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [stage]);

  function close(): void {
    stopStream();
    setStage({ kind: 'starting' });
    onClose();
  }

  async function takePhoto(): Promise<void> {
    const video = videoRef.current;
    if (video === null) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context === null) {
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, MIME_TYPE, QUALITY);
    });
    if (blob === null) {
      return;
    }
    // The camera is released as soon as the frame is taken, rather than when
    // the person confirms: nothing further needs it, and the indicator staying
    // lit through a review screen looks like it is still recording.
    stopStream();

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/gu, '-');
    const file = new File([blob], `bilag-${stamp}.jpg`, { type: MIME_TYPE });
    setStage({ kind: 'review', url: URL.createObjectURL(blob), file });
  }

  function retake(): void {
    setStage({ kind: 'starting' });
    // Re-running the start effect is what re-opens the camera; toggling the
    // stage back to `starting` is not enough on its own because `opened` has
    // not changed, so the stream is requested again here.
    void navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        setStage({ kind: 'live' });
      })
      .catch(() => {
        setStage({ kind: 'fallback', reason: t('camera.unavailable') });
      });
  }

  function confirm(): void {
    if (stage.kind !== 'review') {
      return;
    }
    onCapture(stage.file);
    close();
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={title ?? t('camera.title')}
      fullScreen
      // A viewfinder on a phone wants the whole screen; the transition is
      // dropped because animating a full-screen modal over a live video stream
      // stutters on the devices this is for.
      transitionProps={{ duration: 0 }}
    >
      <Stack gap="md" h="100%">
        {stage.kind === 'starting' && <Text c="dimmed">{t('camera.starting')}</Text>}

        {stage.kind === 'live' && (
          <>
            <Box
              style={{
                background: 'var(--mantine-color-black)',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
              }}
            >
              <video
                ref={videoRef}
                muted
                playsInline
                aria-label={t('camera.viewfinder')}
                style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
            </Box>
            <Button
              size="xl"
              onClick={() => {
                void takePhoto();
              }}
            >
              {t('camera.take')}
            </Button>
          </>
        )}

        {stage.kind === 'review' && (
          <>
            <Box
              style={{
                background: 'var(--mantine-color-black)',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
              }}
            >
              <img
                src={stage.url}
                alt={t('camera.previewAlt')}
                style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
            </Box>
            <Text size="sm" c="dimmed">
              {t('camera.reviewHelp')}
            </Text>
            {/*
              Confirm first and largest. Retake is the safe action and needs no
              emphasis; sending a blurred receipt is the one that costs an audit
              later, so the readable-check is what the eye lands on.
            */}
            <Group grow>
              <Button variant="default" size="lg" onClick={retake}>
                {t('camera.retake')}
              </Button>
              <Button size="lg" onClick={confirm}>
                {t('camera.use')}
              </Button>
            </Group>
          </>
        )}

        {stage.kind === 'fallback' && (
          <Stack gap="sm">
            <Alert color="blue" variant="light" title={t('camera.fallbackTitle')}>
              {stage.reason}
            </Alert>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              aria-label={t('camera.take')}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                if (file !== undefined) {
                  onCapture(file);
                  close();
                }
              }}
            />
            <Button
              size="xl"
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              {t('camera.openSystemCamera')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
